import buildFullPath from 'axios/lib/core/buildFullPath'
import defaults from 'axios/lib/defaults'
import {
  getCsrfToken,
  getDeviceInfo,
  getCsrfKey,
  getContentType,
  getBaseURL,
  getCsrfPos,
  getAppRequestData
} from './process'
import { isAndroid, isFormData, isNode, isBase64, isBlob, blob2Base64, paramSerializer } from './utils'
import { DEF_CONST } from './enum'

/**
 * 要对参数进行排序
 */
export const convertRequestURL = (path, data, method, option, config, runtime) => {
  let fullPath = buildFullPath(getBaseURL(option, config), path)
  if (!/^http/.test(fullPath)) {
    fullPath = `${isNode ? 'http:' : window.location.protocol}//${fullPath}`.replace('////', '//')
  }

  /**
   * 1. POST 请求不在 url 后拼参数
   * 2. 参数为空时，不拼参数
   * 3. 当 bridge 是 v3 版本，参数在 params 里传递，不拼在 url 后面
   */
  if (method === DEF_CONST.method.POST || !Object.keys(data).length || runtime.version === 3) {
    return fullPath
  }

  /**
   * 如果 url 上带了 query 参数，那么把参数拿出来，和 params 合并进行 encode 和 sort
   */
  const query = {}
  if (/\?/.test(fullPath)) {
    fullPath
      .split('?')[1]
      .split('&')
      .map((_) => _.split('='))
      .forEach((kv) => {
        query[kv[0]] = kv[1]
      })
  }

  const params = paramSerializer({ ...data, ...query }, config, runtime)

  return `${fullPath}${/\?/.test(fullPath) ? '&' : '?'}${params}`
}

/**
 * v4 版本的 iOS bridge 有 bug，POST 参数不能为空
 * 这里也要支持 CSRF 的注入和设备信息的注入
 */
export const appendRequestData = async (data, method, option, config, runtime) => {
  let append = {}
  if (runtime.version !== 2 && runtime.version !== 3 && getCsrfPos(option, config) === DEF_CONST.csrfPos.params) {
    const tokenKey = getCsrfKey(option, config)
    const tokenVal = await getCsrfToken()
    if (tokenKey && tokenVal) {
      append[tokenKey] = tokenVal
    }
  }

  const tsParams = getAppRequestData(option, config, runtime)
  const info = await getDeviceInfo(option, config)
  append = {
    ...append,
    ...info,
    ...tsParams
  }

  if (!isAndroid && runtime.version === 4 && method === DEF_CONST.method.POST) {
    append._bridge_v4_fixed_ = '1'
  }

  let result
  if (isFormData(data)) {
    Object.keys(append).forEach((key) => {
      data.append(key, append[key])
    })
    result = data
  } else {
    result = {
      ...append,
      ...data
    }
  }

  return Promise.resolve(result)
}

/**
 * 对于 v1 版本的 bridge，存在 data 多次 encode 的问题
 */
export const stringifyPostData = (dataObj, option, config, runtime) => {
  const contentType = getContentType(option, config, DEF_CONST.method.POST)
  if (
    contentType === DEF_CONST.contentType.FILE ||
    contentType === DEF_CONST.contentType.JSON ||
    !dataObj ||
    !Object.keys(dataObj).length
  ) {
    let ret = dataObj
    if (config.dataSerializer) {
      ret = config.dataSerializer(dataObj)
    }

    if (contentType === DEF_CONST.contentType.JSON && runtime.version) {
      return JSON.stringify(ret)
    }

    return ret
  }

  return paramSerializer(dataObj, config, runtime)
}

export const convertRequestHeader = async (method, option, config, runtime) => {
  const result = {
    ...defaults.headers.common,
    ...(config.headers || {})
  }

  if (runtime.version) {
    result.Referer = window.location.href
    result['User-Agent'] = window.navigator.userAgent
    result[DEF_CONST.contentType.KEY] = getContentType(option, config, method)
  }

  if (getCsrfPos(option, config) === DEF_CONST.csrfPos.headers) {
    const tokenVal = await getCsrfToken()
    const tokenKey = getCsrfKey(option, config)
    if (tokenKey && tokenVal) {
      result[tokenKey] = tokenVal
    }
  }

  return result
}

export const convertFormData = async (data, option, config, runtime) => {
  if (!isFormData(data)) {
    return null
  }

  let fileKey = ''
  let fileName = ''
  let fileValue = ''
  const dataObj = {}
  ;[...data.keys()].forEach((key) => {
    const val = data.get(key)
    if (isBlob(val) || isBase64(val)) {
      fileKey = key
      fileValue = val
      if (isBlob(fileValue)) {
        fileName = fileValue.name
      } else {
        fileName = `upload.${fileValue.substring('data:image/'.length, fileValue.indexOf(';base64'))}`
      }
    } else {
      dataObj[key] = val
    }
  })

  if (!fileKey) {
    return null
  }
  /**
   * v1 版本的 bridge 限定了图片 key 是 file
   */
  if (fileKey !== 'file' && !runtime.v2) {
    return null
  }

  if (isBlob(fileValue)) {
    fileValue = await blob2Base64(fileValue)
  }

  if (getCsrfPos(option, config) === DEF_CONST.csrfPos.params) {
    const tokenKey = getCsrfKey(option, config)
    const tokenVal = await getCsrfToken()
    if (tokenKey && tokenVal) {
      dataObj[tokenKey] = tokenVal
    }
  }

  return {
    fileKey,
    fileName,
    fileValue,
    dataObj
  }
}
